import {
  cargoAmountToSystemUnit,
  getCargoUnitQuantity,
} from '../../constants/cargoUnits';
import { volumeUnitToConstant } from '../../constants/VolumeUnits';
import type { Currency } from '../../graphql';
import { PaymentUnit } from '../../graphql';
import { exhaustiveCheck } from '../../lib/util';

export type CargoToMatch = {
  assortmentId: number;
  dischargePortId: number;
  loadPortId: number;
  mass: number;
  volume: number;
  pcs: number;
};

export type CoaPrice = {
  assortmentGroupId: number | null;
  assortmentId: number | null;
  cargoUnit: string | null;
  currency: Currency;
  dischargePortId: number | null;
  dischargeZoneId: number | null;
  loadPortId: number | null;
  loadZoneId: number | null;
  maxAmount: number | null;
  minAmount: number | null;
  paymentUnit: PaymentUnit;
  price: number;
};

const ASSORTMENT_PREFIX = 'a-';
const ASSORTMENT_GROUP_PREFIX = 'g-';
const LOCATION_PREFIX = 'l-';
const ZONE_LOCATION_PREFIX = 'z-';

type AssortmentKey =
  | `${typeof ASSORTMENT_PREFIX}${number}`
  | `${typeof ASSORTMENT_GROUP_PREFIX}${number}`
  | null;

type LocationKey =
  | `${typeof LOCATION_PREFIX}${number}`
  | `${typeof ZONE_LOCATION_PREFIX}${number}`
  | null;

export type CoaPriceIndex = Map<
  AssortmentKey,
  Map<
    /* by load key */
    LocationKey,
    /* by discharge key */
    Map<LocationKey, CoaPrice[]>
  >
>;

/**
 * Creates a list of prefixed keys. Prefix is used to allow prices
 * defined for a single location take precedence over prices defined
 * for zones
 */
function getLocationKeys(
  locationId: number | null,
  zoneId: number | null,
  locationsByZoneId: Map<number, number[]>,
): LocationKey[] {
  if (locationId !== null) {
    return [`${LOCATION_PREFIX}${locationId}`];
  }

  if (zoneId !== null) {
    const zoneLocationIds = locationsByZoneId.get(zoneId) ?? [];
    return zoneLocationIds.map<LocationKey>(
      (zoneLocationId) => `${ZONE_LOCATION_PREFIX}${zoneLocationId}`,
    );
  }

  return [null];
}

function getAssortmentKeys(
  assortmentId: number | null,
  assortmentGroupId: number | null,
  assortmentsByAssortmentGroup: Map<number, number[]>,
): AssortmentKey[] {
  if (assortmentId !== null) {
    return [`${ASSORTMENT_PREFIX}${assortmentId}`];
  }

  if (assortmentGroupId !== null) {
    const groupAssortmentIds =
      assortmentsByAssortmentGroup.get(assortmentGroupId) ?? [];
    return groupAssortmentIds.map<AssortmentKey>(
      (groupAssortmentId) => `${ASSORTMENT_GROUP_PREFIX}${groupAssortmentId}`,
    );
  }

  return [null];
}

/**
 * Builds an index from the list of COA price rules
 */
export function buildCoaPriceIndex(
  prices: CoaPrice[],
  assortmentsByAssortmentGroup: Map<number, number[]>,
  locationsByZoneId: Map<number, number[]>,
): CoaPriceIndex {
  const index: CoaPriceIndex = new Map();

  for (const price of prices) {
    const assortmentKeys = getAssortmentKeys(
      price.assortmentId,
      price.assortmentGroupId,
      assortmentsByAssortmentGroup,
    );

    for (const assortmentKey of assortmentKeys) {
      const byLoadKey = index.get(assortmentKey) ?? new Map();
      index.set(assortmentKey, byLoadKey);

      const loadKeys = getLocationKeys(
        price.loadPortId,
        price.loadZoneId,
        locationsByZoneId,
      );
      const dischargeKeys = getLocationKeys(
        price.dischargePortId,
        price.dischargeZoneId,
        locationsByZoneId,
      );

      for (const loadKey of loadKeys) {
        const byDischargeKey = byLoadKey.get(loadKey) ?? new Map();
        byLoadKey.set(loadKey, byDischargeKey);

        for (const dischargeKey of dischargeKeys) {
          const priceList = byDischargeKey.get(dischargeKey) ?? [];
          byDischargeKey.set(dischargeKey, priceList);

          priceList.push(price);
        }
      }
    }
  }

  // Sort prices so that more specific ones are matched first
  for (const loadPortMap of index.values()) {
    for (const dischargePortMap of loadPortMap.values()) {
      for (const rules of dischargePortMap.values()) {
        rules.sort((a, b) => {
          const aSpecificity =
            (a.minAmount !== null ? 1 : 0) + (a.maxAmount !== null ? 1 : 0);
          const bSpecificity =
            (b.minAmount !== null ? 1 : 0) + (b.maxAmount !== null ? 1 : 0);
          return bSpecificity - aSpecificity;
        });
      }
    }
  }

  return index;
}

/**
 * Match given cargo to a price rule using price index
 */
export function getIndexedCoaPrice(
  cargo: CargoToMatch,
  priceIndex: CoaPriceIndex,
): CoaPrice | null {
  const { assortmentId, loadPortId, dischargePortId, mass, volume } = cargo;

  const assortmentKeys: AssortmentKey[] = [
    `${ASSORTMENT_PREFIX}${assortmentId}`,
    `${ASSORTMENT_GROUP_PREFIX}${assortmentId}`,
    null,
  ];
  const loadPortKeys: LocationKey[] = [
    `${LOCATION_PREFIX}${loadPortId}`,
    `${ZONE_LOCATION_PREFIX}${loadPortId}`,
    null,
  ];
  const dischargePortKeys: LocationKey[] = [
    `${LOCATION_PREFIX}${dischargePortId}`,
    `${ZONE_LOCATION_PREFIX}${dischargePortId}`,
    null,
  ];

  for (const assortmentKey of assortmentKeys) {
    const loadPortMap = priceIndex.get(assortmentKey);
    if (!loadPortMap) continue;

    for (const loadPortKey of loadPortKeys) {
      const dischargePortMap = loadPortMap.get(loadPortKey);
      if (!dischargePortMap) continue;

      for (const dischargePortKey of dischargePortKeys) {
        const prices = dischargePortMap.get(dischargePortKey);
        if (!prices) continue;

        for (const price of prices) {
          const amountMatches = matchCoaAmountRange(
            {
              mass,
              volume,
            },
            {
              cargoUnit: price.cargoUnit,
              maxAmount: price.maxAmount,
              minAmount: price.minAmount,
              paymentUnit: price.paymentUnit,
            },
          );

          if (amountMatches) {
            return price;
          }
        }
      }
    }
  }

  return null;
}

/**
 * COA amount range is expected to be start inclusive - end inclusive
 */
export function matchCoaAmountRange(
  cargo: {
    mass: number;
    volume: number;
  },
  price: {
    cargoUnit: string | null;
    maxAmount: number | null;
    minAmount: number | null;
    paymentUnit: PaymentUnit;
  },
) {
  const { maxAmount, minAmount, paymentUnit } = price;

  switch (paymentUnit) {
    case PaymentUnit.LUMPSUM:
    case PaymentUnit.PER_SET:
    case PaymentUnit.PER_PCS: {
      const cargoUnit = price.cargoUnit ?? 't';
      const quantity = getCargoUnitQuantity(cargoUnit) ?? 'mass';
      const amount = quantity === 'mass' ? cargo.mass : cargo.volume;
      return (
        (!minAmount ||
          amount >= cargoAmountToSystemUnit(minAmount, cargoUnit)) &&
        (!maxAmount || amount <= cargoAmountToSystemUnit(maxAmount, cargoUnit))
      );
    }
    case PaymentUnit.M3:
    case PaymentUnit.SCBM_UB: {
      return (
        (!minAmount || cargo.volume >= minAmount) &&
        (!maxAmount || cargo.volume <= maxAmount)
      );
    }
    case PaymentUnit.MT:
    case PaymentUnit.BDMT: {
      return (
        (!minAmount || cargo.mass >= minAmount) &&
        (!maxAmount || cargo.mass <= maxAmount)
      );
    }
    case PaymentUnit.FT3: {
      const volume = cargo.volume * volumeUnitToConstant.FT_3;
      return (
        (!minAmount || volume >= minAmount) &&
        (!maxAmount || volume <= maxAmount)
      );
    }
    case PaymentUnit.WORLDSCALE: {
      // true for flatrate
      return true;
    }
    default: {
      exhaustiveCheck(paymentUnit);
    }
  }
}
